import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { logo } from '@/assets';
import { UPCOMING } from '@/data/events';
import { getCountdown, type Countdown } from '@/lib/format';
import { useI18n } from '@/providers/i18n';
import CartLink from '@/ui/CartLink';
import LangSwitcher from '@/ui/LangSwitcher';

import MenuOverlay from './MenuOverlay';
import styles from './Nav.module.css';

const NEXT_EVENT = UPCOMING.find((e) => e.flagship) ?? UPCOMING[0];

export default function Nav() {
  const { t } = useI18n();
  const [tick, setTick] = useState<Countdown>(() =>
    NEXT_EVENT ? getCountdown(NEXT_EVENT.date) : { d: '000', h: '00', m: '00', s: '00' },
  );
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!NEXT_EVENT) return;
    const id = window.setInterval(() => setTick(getCountdown(NEXT_EVENT.date)), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const delta = y - lastY;
      if (y > 120 && delta > 4) setHidden(true);
      else if (delta < -4 || y < 80) setHidden(false);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/events', label: t('nav.events') },
    { to: '/artists', label: t('nav.artists') },
    { to: '/shop', label: t('nav.shop') },
    { to: '/contact', label: t('nav.contact') },
  ];

  const countdownLabel = `T-${tick.d}:${tick.h}:${tick.m}:${tick.s}`;

  return (
    <>
      <nav
        className={`${styles.bar} ${hidden ? styles.hidden : ''}`}
        data-variant="a"
      >
        <div className={`${styles.row} ${styles.rowMain}`}>
          <div className={styles.left}>
            <Link to="/" className={styles.brand} aria-label="MOCT home">
              <img src={logo} alt="MOCT" />
            </Link>
          </div>

          <div className={styles.center}>
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `${styles.link} ${isActive ? styles.active : ''}`}
              >
                <span className={styles.linkLabel}>{l.label}</span>
              </NavLink>
            ))}
          </div>

          <div className={styles.right}>
            <LangSwitcher />
            <CartLink />
            <button
              type="button"
              className={styles.menuBtn}
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              <span aria-hidden="true">≡</span>
            </button>
          </div>
        </div>
      </nav>

      <MenuOverlay
        open={open}
        onClose={() => setOpen(false)}
        links={links}
        countdownLabel={countdownLabel}
      />
    </>
  );
}
